import { LegalDoc } from '../legal-doc.model';

/** English version — mirrors chroniq-privacy.de.ts section for section. */
export const CHRONIQ_PRIVACY_EN: LegalDoc = {
    title: 'Chroniq Privacy Policy',
    meta: 'As at 3 November 2026 · Applies to the Chroniq app (iOS, web) and chroniq.ch',
    tocHeading: 'Contents',
    footer: 'The German version prevails. Translations are provided for convenience.',
    sections: [
        {
            num: '01',
            title: 'Allocation of roles: controller and processor',
            body: `
        <p>Chroniq is a multi-tenant software-as-a-service solution made available to businesses (the
        "customer" or "employer") for time tracking and staff administration.</p>
        <p>When employee data is processed — time tracking, shift planning, absences, chat, employee
        profile — <strong>the employer concerned is the controller</strong> under the Swiss Federal Act on
        Data Protection (FADP) and, where applicable, the General Data Protection Regulation (GDPR).
        <strong>Gastro Management Oberbillig</strong> ("the operator", "we") acts as
        <strong>processor</strong> under Art. 9 FADP and Art. 28 GDPR, on the basis of a data processing
        agreement concluded with every customer.</p>
        <p><strong>We are ourselves the controller</strong> for operating, securing and developing the
        platform, including technical diagnostic data (section 09), for managing the customer relationship
        and for our website.</p>
        <p>This policy provides comprehensive information about processing within Chroniq. Employers may
        also use it as a basis for their own duty to inform employees (Art. 328b CO, Art. 19 FADP,
        Art. 13 f. GDPR). Employees should address questions about a specific processing operation to their
        employer first; we are also available for questions about the platform:</p>
        <p><strong>Gastro Management Oberbillig</strong><br>
        Sole proprietorship, owner: Erik Alexander Oberbillig<br>
        Operator of the Chroniq app<br>
        Kirchenrütti 14, 6463 Bürglen UR, Switzerland</p>
        <p>Chroniq privacy contact: <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a><br>
        Central privacy contact for the group: <a href="mailto:privacy@gastro-management.ch">privacy&#64;gastro-management.ch</a></p>
      `,
        },
        {
            num: '02',
            title: 'Sign-in data',
            body: `
        <p>Authentication runs through <strong>Auth0</strong> on an EU tenant (<code>*.eu.auth0.com</code>)
        with data held in the EU. The provider is Okta, Inc. The following are processed:</p>
        <ul>
          <li>email address</li>
          <li>first and last name</li>
          <li>profile picture (optional, from the authentication token)</li>
          <li>user identifier (Auth0 subject identifier)</li>
          <li>access role (<em>employee</em>, <em>chef</em>, <em>admin</em>)</li>
          <li>onboarding status</li>
        </ul>
        <p>Auth0 is engaged as a processor; a data processing agreement with Okta, Inc. is in place. As the
        parent company is subject to US law and access from the USA in the course of support and
        maintenance cannot be entirely ruled out, this potential third-country element is covered by the EU
        Standard Contractual Clauses (section 13).</p>
        <p><strong>We do not store passwords.</strong></p>
      `,
        },
        {
            num: '03',
            title: 'Time tracking data',
            body: `
        <p>The app records:</p>
        <ul>
          <li>start, end and date of work sessions</li>
          <li>breaks (start, end, duration)</li>
          <li>work status (running, paused, completed)</li>
          <li>optional notes on a session</li>
          <li>daily and weekly working time figures and the overtime balance</li>
        </ul>
        <p>This includes time corrections and subsequent entries with their reasons and the supervisor's
        decision, as well as the month-end closing. A <strong>change log</strong> records, for write changes to
        staff and time data, who changed what and when (user identifier and role of the acting person, time,
        affected record, changed fields). Sensitive fields such as the OASI number or IBAN are not stored in the
        log in clear text. The log serves traceability towards the employer and security; it is not used to
        monitor behaviour or performance (section 19).</p>
        <p>This processing serves the employer's record-keeping duty under Art. 46 of the Employment Act
        and Art. 73 of Ordinance 1 to the Employment Act, and the correct calculation of pay.</p>
        <p><strong>No location tracking:</strong> Chroniq collects <strong>no</strong> location or position
        data from the device when clocking in or out and evaluates no movement profiles.</p>
      `,
        },
        {
            num: '04',
            title: 'Time tracking via badge terminals',
            body: `
        <p>Where an employer uses physical RFID badge terminals, scan events (badge identifier, device
        identifier, timestamp) are processed through Microsoft Azure IoT Hub, attributed to an employee and
        recorded as a clock-in or clock-out; a short acknowledgement is returned to the terminal. Breaks can also be scanned at the terminal (break, end break).</p>
        <p>The badge identifier is a plain card number and contains no biometric characteristics.</p>
        <p>After a scan the terminal briefly shows the name, the result and, unless the business has
        switched this off, the daily total ("Worked today" and "Breaks"). So that the terminal also works
        without a network connection, it stores the names of badge holders and scans not yet sent in
        encrypted form locally on the device. The operator can access the device remotely for maintenance.
        Retention of time-tracking data follows section 11.</p>
        <p>Instead of a physical card, a digital badge in Apple Wallet may also be used (section 08).</p>
        <p><em>This feature is in pilot operation</em> and is enabled only at individual businesses that
        expressly use the gateway terminal.</p>
      `,
        },
        {
            num: '05',
            title: 'Shift planning and absences',
            body: `
        <p>Supervisors create shifts and decide on absence requests. The following are processed:</p>
        <ul>
          <li>shift times, shift templates and calendar exports (iCal)</li>
          <li>absence type (<em>holiday</em>, <em>sickness</em>, <em>accident</em>,
              <em>maternity/paternity</em>, <em>military service</em>, <em>training</em>,
              <em>compensation day</em>, <em>other</em>) as a mere category with no diagnosis or certificate
              field, plus period, status and holiday account</li>
          <li>shift swap requests between employees and the supervisor's decision on them</li>
          <li>the free-text fields "note" and "reason for rejection"</li>
        </ul>
        <p><strong>Note on data minimisation:</strong> the "sickness", "accident" and "maternity/paternity" categories technically record
        no diagnosis. The free-text fields, however, are unchecked plain text. Employees and supervisors are
        asked not to enter health information or other sensitive personal data (Art. 5(c) FADP /
        Art. 9 GDPR) there unless expressly necessary. Where such data is entered nonetheless, we process it
        on behalf of the employer under the same access restrictions as other staff data.</p>
      `,
        },
        {
            num: '06',
            title: 'Employee data and profile',
            body: `
        <p>The system holds the following staff data:</p>
        <ul>
          <li>name, email address, telephone number</li>
          <li>staff number, job title, department, start date</li>
          <li>invitation status (pending, accepted, revoked)</li>
          <li>optional profile picture in a private storage container, retrievable only through
              time-limited, signed addresses</li>
        </ul>
        <p>Only people holding the <em>chef</em> or <em>admin</em> role can additionally process:</p>
        <ul>
          <li>OASI (AHV) number</li>
          <li>IBAN or bank details</li>
          <li>date of birth</li>
          <li>home address</li>
          <li>emergency contact (name, relationship, telephone number)</li>
        </ul>
        <p>Under Art. 328b CO an employer may only process data concerning an employee's suitability for
        the job or necessary for performing the employment contract. The employer is responsible for
        completing only such fields.</p>
      `,
        },
        {
            num: '07',
            title: 'Team chat and announcements',
            body: `
        <p>Where the chat feature is enabled, the following are processed:</p>
        <ul>
          <li>message content, sender, timestamp and edit/delete status. Deleted messages are removed from
              the display; the content technically remains in storage (soft delete) and is finally removed
              when the account is anonymised or on the employer's instruction.</li>
          <li>read receipts per user and message</li>
          <li>file attachments (PDF, JPEG, PNG, max. 20 MB) in a private storage container, retrievable
              only through signed addresses valid for one hour</li>
          <li>announcements per business: messages written by supervisors or administrators that all
              employees of the business can read</li>
        </ul>
        <p>The chat serves internal business communication. No systematic evaluation of communication
        content for behavioural or performance monitoring takes place, and the employer is prohibited from
        doing so under section 19.</p>
      `,
        },
        {
            num: '08',
            title: 'Notifications and Apple Wallet',
            body: `
        <p>On iOS devices, shift changes, shift swap requests, time corrections and decisions on absence requests trigger a
        push notification via
        the <strong>Apple Push Notification Service</strong>. For this we store a push token per device
        together with a device identifier; both are deleted on sign-out. Employees can unsubscribe from
        notifications in the profile settings and in the device's system settings. The web application
        delivers no push notifications; there, notices appear only inside the open application.</p>
        <p>Where the employer has enabled the overtime notification, employees receive an email with their overtime
        balance at the start of each month. It is sent through Azure Communication Services (section 12).</p>
        <p>Where the Wallet feature is enabled, the backend generates a signed <em>.pkpass</em> document as
        a staff pass. It is stored locally on the device; only the device's registration status for
        automatic updates remains in the system.</p>
      `,
        },
        {
            num: '09',
            title: 'Technical connection and diagnostic data',
            body: `
        <ul>
          <li>real-time connection data through <strong>Azure SignalR Service</strong> for live updates</li>
          <li>request metadata (IP address, address called, timestamp, status code) and error logs,
              processed by <strong>Azure Application Insights</strong> for operational, diagnostic and
              security purposes</li>
        </ul>
        <p>This processing takes place on Azure infrastructure in the EU. We are the controller for this
        data (section 01).</p>
      `,
        },
        {
            num: '10',
            title: 'Purposes and legal bases',
            body: `
        <ul>
          <li><strong>Performance of a contract</strong> (Art. 31(2)(a) FADP / Art. 6(1)(b) GDPR):
              providing the time tracking and staff administration service to the employer and performing
              the employment contract.</li>
          <li><strong>Legal obligation</strong> (Art. 6(1)(c) GDPR): meeting employment law record-keeping
              and retention duties under Art. 46 Employment Act, Art. 73 Ordinance 1 to the Employment Act,
              and accounting duties under the Code of Obligations.</li>
          <li><strong>Legitimate interests</strong> (Art. 31(1) FADP / Art. 6(1)(f) GDPR): operating,
              securing and protecting the platform against misuse, including technical diagnostic data
              (section 09).</li>
          <li><strong>Consent</strong> (Art. 6(6) FADP / Art. 6(1)(a) GDPR): purely optional features such
              as profile picture, push notifications and Apple Wallet, which employees activate
              themselves. Consent in an employment relationship is valid only if given freely; refusing it
              puts employees at no disadvantage and it can be withdrawn at any time.</li>
        </ul>
      `,
        },
        {
            num: '11',
            title: 'Retention and deletion',
            body: `
        <p>Data is held exclusively on <strong>Microsoft Azure</strong> infrastructure in the EU (primarily
        West Europe).</p>
        <ul>
          <li><strong>Time tracking and shift data:</strong> at least 5 years after they cease to be
              current (Art. 73(2) Ordinance 1 to the Employment Act); then deleted or anonymised on the
              employer's instruction or when the contract ends. Automatic deletion after the period
              expires is not currently in place.</li>
          <li><strong>Change log (section 03):</strong> labelled by retention category (session data 90 days,
              operational data 2 years, legally relevant data 10 years). Automatic deletion is not
              currently in place.</li>
          <li><strong>Records of accounting relevance:</strong> 10 years (Art. 958f CO).</li>
          <li><strong>Staff data:</strong> until the employment relationship ends and thereafter only for as
              long as a legal duty or an enforceable claim requires.</li>
          <li><strong>Chat and announcement messages:</strong> until deleted by authorised people or until
              the associated account is anonymised.</li>
          <li><strong>Technical diagnostic data (section 09):</strong> according to the default retention of
              Azure Application Insights, generally up to 90 days.</li>
        </ul>
        <p>The employer may instruct shorter periods for the data it controls. After the customer contract
        ends we delete or return the data as it instructs.</p>
      `,
        },
        {
            num: '12',
            title: 'Recipients and processors',
            body: `
        <p>Data is disclosed only to processors contractually bound to comply with data protection law:</p>
        <ul>
          <li><strong>Okta, Inc. (Auth0)</strong> — authentication, EU tenant with data held in the EU;
              US parent company, covered by Standard Contractual Clauses (sections 02, 13)</li>
          <li><strong>Microsoft Ireland Operations Limited (Azure)</strong> — cloud infrastructure, database
              and storage, SignalR, Application Insights, delivery of transactional email (Azure
              Communication Services, in particular invitations to employees) and, where enabled, IoT Hub
              for badge terminals; EU data centres, primarily West Europe</li>
          <li><strong>Apple Inc.</strong> — delivery of push notifications and, where the Wallet feature is
              enabled, provision of the pass on the device</li>
          <li><strong>Cloudflare, Inc.</strong> — name resolution (DNS) and forwarding and protection of traffic to
              chroniq.ch; request data (in particular the IP address) is routed through Cloudflare's global network,
              including outside the EU; covered by Standard Contractual Clauses (section 13)</li>
        </ul>
        <p>There is no disclosure to other third parties, no use for advertising, no sale of personal data
        and no use for training AI models. A complete list of sub-processors is available on request; new
        sub-processors are announced to customers in advance.</p>
      `,
        },
        {
            num: '13',
            title: 'Processing abroad',
            body: `
        <p>Data is held in the EU. Individual providers are US companies subject to US law; access from the
        USA in the course of support and maintenance cannot be entirely ruled out.</p>
        <p>Such transfers rely on the European Commission's <strong>Standard Contractual Clauses</strong>
        (Art. 46(2)(c) GDPR) in conjunction with Art. 16(2)(d) FADP, with the amendments required for
        Switzerland, supplemented by the adequacy decision on the <strong>EU–U.S. Data Privacy
        Framework</strong> and the Swiss recognition of the <strong>Swiss–U.S. Data Privacy Framework</strong>
        where the provider is certified. We deliberately maintain the Standard Contractual Clauses as an
        independent basis so that transfers remain covered should the adequacy decision cease to apply.</p>
        <p>A copy of the safeguards is available on request:
        <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a></p>
      `,
        },
        {
            num: '14',
            title: 'Data security',
            body: `
        <ul>
          <li>All transmissions are encrypted via TLS/HTTPS; data at rest is encrypted.</li>
          <li>Access is role-based; sensitive fields are visible only to authorised roles, additionally
              protected by tenant-specific row-level filtering in the database.</li>
          <li>File attachments and profile pictures reside in private storage containers without anonymous
              access and are delivered only through time-limited, signed addresses.</li>
          <li>Authentication runs exclusively through Auth0 with OAuth 2.0 / OIDC.</li>
          <li>In the web portal, sign-in tokens are kept in the browser's local storage until you sign out
              (technically necessary so that the session survives a page reload); on native iOS devices,
              security-critical values are held in the operating system's key store.</li>
        </ul>
        <p>We report data security breaches to the FDPIC as soon as possible (Art. 24 FADP). As we are the
        processor for employee data, we also inform the employer concerned without delay so that it can meet
        its own notification duties — within 72 hours under Art. 33 GDPR.</p>
      `,
        },
        {
            num: '15',
            title: 'Cookies and local storage',
            body: `
        <p>The website and app use no third-party marketing or analytics cookies. Only technically necessary
        local storage is used:</p>
        <ul>
          <li>appearance preference (light or dark mode)</li>
          <li>in the web portal, the sign-in tokens (until sign-out) and, for administrators, the selected
              business, so that the session survives a page reload</li>
          <li>caching of running time entries and a queue for entries made without a network connection,
              which is synchronised automatically on reconnection</li>
        </ul>
        <p>No consent is required for these technically necessary operations.</p>
      `,
        },
        {
            num: '16',
            title: 'Your rights',
            body: `
        <p>As a data subject you have the right of access, rectification, erasure, restriction of
        processing, data portability and objection to processing based on a legitimate interest
        (Art. 25 ff. FADP / Art. 15 ff. GDPR). You may withdraw any consent given at any time with effect
        for the future.</p>
        <p>As we act as the employer's processor for staff data, we forward requests concerning such data to
        the responsible employer or handle them on its instructions. <strong>Please address such matters to
        your employer first.</strong></p>
        <p>We implement an erasure request technically by <strong>anonymising</strong> the employee account:
        name, contact, bank and other sensitive data are irreversibly removed from the staff record. We
        remove the profile picture, push registrations and the sign-in account at the authentication service
        separately on the employer's instruction. Time tracking, shift and audit data
        remain in anonymised form for statutory retention reasons (Art. 46 Employment Act, Art. 73
        Ordinance 1 to the Employment Act, Code of Obligations). Chat messages already sent remain visible
        under the name valid at the time of sending, because that name is stored historically within the
        message.</p>
        <p>Contact: <a href="mailto:datenschutz@chroniq.ch">datenschutz&#64;chroniq.ch</a></p>
        <p>You may also complain to the <strong>Federal Data Protection and Information Commissioner
        (FDPIC)</strong>, Feldeggweg 1, 3003 Bern:
        <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">www.edoeb.admin.ch</a>.
        Where the processing falls under the GDPR, you additionally have the right to complain to the
        supervisory authority of your residence or place of work (Art. 77 GDPR).</p>
      `,
        },
        {
            num: '17',
            title: 'International customers and applicable law',
            body: `
        <p>Chroniq is used in Switzerland and internationally. We apply a tiered regime:</p>
        <ul>
          <li><strong>Switzerland:</strong> the FADP applies as the law of our country of establishment.</li>
          <li><strong>EU/EEA:</strong> the GDPR applies to data subjects in the EU/EEA in so far as we
              provide services to businesses established there or monitor the behaviour of people in the
              EU/EEA (Art. 3(2) GDPR). Switzerland benefits from an EU adequacy decision, so transfers
              between the EU/EEA and our Swiss infrastructure require no additional safeguards. Where
              Art. 27 GDPR so requires, we appoint a representative in the EU; those contact details will be
              added here once appointed.</li>
          <li><strong>United Kingdom:</strong> the UK GDPR applies accordingly.</li>
          <li><strong>Other countries:</strong> we comply with the applicable local rules in so far as they
              affect our service; country-specific addenda are available on request.</li>
        </ul>
      `,
        },
        {
            num: '18',
            title: 'Artificial intelligence',
            body: `
        <p>Chroniq uses <strong>no</strong> artificial intelligence systems within the meaning of
        Regulation (EU) 2024/1689 (the "AI Act"). Shift planning, overtime calculation, the holiday account
        and the handling of absence requests rest exclusively on hard-coded, rule-based logic.</p>
        <p>The AI Act has been generally applicable since 2 August 2026. It is particularly strict for the
        employment context: AI systems that allocate work-related tasks based on individual behaviour or
        personal traits, or that monitor and evaluate the performance and behaviour of employees, count as
        <strong>high-risk systems</strong> under Annex III point 4. Emotion recognition in the workplace has
        been <strong>prohibited</strong> under Art. 5(1)(f) since 2 February 2025. Chroniq contains no such
        function.</p>
        <p>Should an AI component be introduced in future — for instance as part of a reporting or
        forecasting feature — we will classify it under the AI Act beforehand and update this policy before
        it goes live. For a high-risk system the employer, as deployer, would additionally have to meet the
        obligations of Art. 26, in particular informing workers' representatives and the employees concerned
        before putting the system into service (Art. 26(7)).</p>
      `,
        },
        {
            num: '19',
            title: 'No behavioural monitoring, no automated decisions',
            body: `
        <p>Chroniq takes no decisions based solely on automated processing, including profiling, that
        produce legal effects concerning employees or similarly significantly affect them (Art. 21 FADP /
        Art. 22 GDPR). Absence requests, shift allocation, warnings and the termination of an employment
        relationship are always decided by a human being.</p>
        <p>Under Art. 26(1) of Ordinance 3 to the Employment Act, monitoring or control systems intended to
        monitor employee behaviour at the workplace <strong>may not</strong> be used. Chroniq is intended
        for recording working time and organising assignments, not for behavioural surveillance. The
        employer contractually undertakes not to use the reporting functions for continuous behavioural or
        performance monitoring.</p>
      `,
        },
        {
            num: '20',
            title: 'Minors',
            body: `
        <p>Chroniq is addressed to employees within an employment relationship. The employer is responsible
        for use by employees who are minors, such as apprentices; the consent of their legal representative
        may be required. The special protective rules for young employees under Art. 29 ff. of the
        Employment Act and the Ordinance on the Protection of Young Workers are reserved and must be
        observed by the employer.</p>
      `,
        },
        {
            num: '21',
            title: 'Changes to this policy',
            body: `
        <p>We review this policy regularly and adapt it when the legal position or our processing changes.
        The version in force is available at
        <a href="https://www.gastro-management.ch/privacy?lang=en#chroniq">gastro-management.ch/privacy</a>.
        We inform registered users of material changes by email. The date of the last revision appears in
        the page header.</p>
      `,
        },
    ],
};
